/*
 * AlertsTab.jsx
 *
 * Full alerts management tab with three sub-views:
 *   LIVE    — active + acknowledged alerts with assignment, notes, and action buttons.
 *   RULES   — alert rule CRUD (min priority, channels, alert types).
 *   HISTORY — resolved alert log with event timeline.
 * Owns its own fetch state; does not share state with the root Dashboard.
 * Receives authHeaders (async fn → headers obj), user, and darkMode as props.
 * Renders DetailPanel inline when an alert is selected.
 * Imported by: Dashboard.jsx.
 */

import { useState, useEffect, useCallback } from "react";
import { API, timeAgo, fmtAlertDate, _parseTs } from "./utils";
import { DetailPanel } from "./DetailPanel";

// ── Theme constants ───────────────────────────────────────────────────────────

const DARK_THEME = {
  bg: "#080808", card: "#0A0A0A", border: "#1E1E1E",
  text: "#E5E5E5", muted: "#666", label: "#444",
  P1: "#FF3B3B", P2: "#FF8C00", P3: "#FFD700", P4: "#00C896",
};
const LIGHT_THEME = {
  bg: "#F9FAFB", card: "#FFFFFF", border: "#E5E7EB",
  text: "#111827", muted: "#6B7280", label: "#9CA3AF",
  P1: "#DC2626", P2: "#F97316", P3: "#D97706", P4: "#059669",
};

const ALERT_TYPE_OPTIONS = [
  "CONFIRMED_FAULT","HARDWARE_FLAPPING","ACTIVE_FLAPPING","INTERMITTENT_FLAPPING",
  "SILENT_RISK","PROACTIVE_MAINTENANCE","EARLY_WARNING",
];

function darkTierColor(tier, theme) {
  const k = (tier || "P4").slice(0, 2);
  return { P1: theme.P1, P2: theme.P2, P3: theme.P3, P4: theme.P4 }[k] || theme.P4;
}

function ruleDescription(rule) {
  const tier  = rule.min_priority || "P4";
  const types = rule.alert_types?.length ? rule.alert_types.join(", ") : "any type";
  const chans = [];
  if (rule.channel_slack)     chans.push("Slack");
  if (rule.channel_dashboard) chans.push("Dashboard");
  const chanStr = chans.length ? chans.join(" + ") : "no channels";
  const extras  = [];
  if ((rule.min_risk_score  || 0) > 0) extras.push(`risk ≥ ${Math.round(rule.min_risk_score * 100)}%`);
  if ((rule.min_state_score || 0) > 0) extras.push(`state ≥ ${Math.round(rule.min_state_score * 100)}%`);
  return `${tier}+ · ${types} → ${chanStr}${extras.length ? " · " + extras.join(", ") : ""}`;
}

const EVENT_ICON = {
  created:       "◆",
  acknowledged:  "✓",
  resolved:      "✔",
  muted:         "⊘",
  assigned:      "→",
  note:          "◎",
  ticket_drafted:"🎫",
};

function EventTimeline({ events, theme }) {
  const T = theme || DARK_THEME;
  if (!events.length) return (
    <div style={{ padding: "16px 0", fontSize: 11, color: T.muted, fontFamily: "'DM Mono', monospace" }}>No events yet.</div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {events.map((ev) => (
        <div key={ev.id} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ fontSize: 13, color: T.P4, flexShrink: 0, marginTop: 1, fontFamily: "'DM Mono', monospace" }}>
            {EVENT_ICON[ev.event_type] || "·"}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: T.text, fontFamily: "'DM Mono', monospace" }}>
              <span style={{ color: T.P4, fontWeight: 700 }}>{ev.event_type}</span>
              {ev.actor ? <span style={{ color: T.muted }}> by {ev.actor}</span> : null}
            </div>
            {ev.detail && <div style={{ fontSize: 11, color: T.muted, marginTop: 2, lineHeight: 1.5 }}>{ev.detail}</div>}
            <div style={{ fontSize: 10, color: T.label, marginTop: 2 }}>{timeAgo(ev.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── AlertsTab ─────────────────────────────────────────────────────────────────

function AlertsTab({ authHeaders, user, darkMode }) {
  const D = darkMode ? DARK_THEME : LIGHT_THEME;
  const [subView,       setSubView]       = useState("LIVE");

  // LIVE
  const [liveAlerts,    setLiveAlerts]    = useState([]);
  const [liveLoading,   setLiveLoading]   = useState(false);
  const [priFilter,     setPriFilter]     = useState("ALL");
  const [typeFilter,    setTypeFilter]    = useState("ALL");
  const [assignFilter,  setAssignFilter]  = useState("ALL");
  const [selAlert,      setSelAlert]      = useState(null);
  const [events,        setEvents]        = useState([]);
  const [evLoading,     setEvLoading]     = useState(false);
  const [assignInput,   setAssignInput]   = useState("");
  const [noteInput,     setNoteInput]     = useState("");
  const [actLoading,    setActLoading]    = useState(false);
  const [ticketMsg,     setTicketMsg]     = useState(null);

  // RULES
  const [rules,         setRules]         = useState([]);
  const [rulesLoading,  setRulesLoading]  = useState(false);
  const [newRule, setNewRule] = useState({
    name: "", min_priority: "P2", alert_types: [],
    min_risk_score: 0, min_state_score: 0, channel_slack: 0, channel_dashboard: 1, channel_email: "",
  });
  const [editingRule,   setEditingRule]   = useState(null);
  const [rulesSaving,   setRulesSaving]   = useState(false);

  // HISTORY
  const [history,       setHistory]       = useState([]);
  const [histLoading,   setHistLoading]   = useState(false);
  const [selHistory,    setSelHistory]    = useState(null);
  const [histEvents,    setHistEvents]    = useState([]);

  const [timeFilter,    setTimeFilter]    = useState("ALL");
  const [alertSearch,   setAlertSearch]   = useState("");
  const [histSearch,    setHistSearch]    = useState("");

  const [alertCharger,        setAlertCharger]        = useState(null);
  const [alertChargerLoading, setAlertChargerLoading] = useState(false);
  const [histCharger,         setHistCharger]         = useState(null);
  const [histChargerLoading,  setHistChargerLoading]  = useState(false);

  const [toast, setToast] = useState(null);

  const actor = user?.primaryEmailAddress?.emailAddress || "dashboard";

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Fetch helpers ─────────────────────────────────────────────────────────

  const fetchLive = useCallback(async () => {
    setLiveLoading(true);
    try {
      const headers = await authHeaders();
      const [r1, r2] = await Promise.all([
        fetch(`${API}/alerts?status=active&limit=300`, { headers }),
        fetch(`${API}/alerts?status=acknowledged&limit=200`, { headers }),
      ]);
      const d1 = r1.ok ? (await r1.json()).alerts || [] : [];
      const d2 = r2.ok ? (await r2.json()).alerts || [] : [];
      setLiveAlerts([...d1, ...d2]);
    } catch {
    } finally {
      setLiveLoading(false);
    }
  }, [authHeaders]);

  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/alert-rules`, { headers });
      if (res.ok) setRules((await res.json()).rules || []);
    } finally {
      setRulesLoading(false);
    }
  }, [authHeaders]);

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/alerts?status=resolved&limit=300`, { headers });
      if (res.ok) setHistory((await res.json()).alerts || []);
    } finally {
      setHistLoading(false);
    }
  }, [authHeaders]);

  const fetchEvents = useCallback(async (alertId, setter) => {
    if (!alertId) return;
    setter === "hist" ? setEvLoading(false) : setEvLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/alerts/${alertId}/events`, { headers });
      const evs = res.ok ? (await res.json()).events || [] : [];
      if (setter === "hist") setHistEvents(evs);
      else setEvents(evs);
    } finally {
      setEvLoading(false);
    }
  }, [authHeaders]);

  // ── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (subView === "LIVE") {
      fetchLive();
      const id = setInterval(fetchLive, 30000);
      return () => clearInterval(id);
    } else if (subView === "RULES") {
      fetchRules();
    } else if (subView === "HISTORY") {
      fetchHistory();
    }
  }, [subView, fetchLive, fetchRules, fetchHistory]);

  useEffect(() => {
    if (selAlert) { setEvents([]); setAssignInput(selAlert.assigned_to || ""); setNoteInput(""); setTicketMsg(null); fetchEvents(selAlert.id); }
  }, [selAlert, fetchEvents]);

  useEffect(() => {
    if (!selAlert) { setAlertCharger(null); return; }
    setAlertChargerLoading(true);
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/score/charger/${selAlert.charger_sn}`, { headers });
        if (res.ok) { const d = await res.json(); if (d?.result) setAlertCharger(d.result); }
      } catch { } finally { setAlertChargerLoading(false); }
    })();
  }, [selAlert, authHeaders]);

  useEffect(() => {
    if (selHistory) { setHistEvents([]); fetchEvents(selHistory.id, "hist"); }
  }, [selHistory, fetchEvents]);

  useEffect(() => {
    if (!selHistory) { setHistCharger(null); return; }
    setHistChargerLoading(true);
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/score/charger/${selHistory.charger_sn}`, { headers });
        if (res.ok) { const d = await res.json(); if (d?.result) setHistCharger(d.result); }
      } catch { } finally { setHistChargerLoading(false); }
    })();
  }, [selHistory, authHeaders]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const doAck = async () => {
    if (!selAlert) return;
    setActLoading(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const res = await fetch(`${API}/alerts/${selAlert.id}/acknowledge`, {
        method: "POST", headers,
        body: JSON.stringify({ acknowledged_by: actor, notes: "" }),
      });
      if (!res.ok) throw new Error();
      setLiveAlerts(a => a.map(x => x.id === selAlert.id ? { ...x, status: "acknowledged" } : x));
      setSelAlert(s => ({ ...s, status: "acknowledged" }));
      await fetchEvents(selAlert.id);
      showToast("Alert acknowledged");
    } catch { showToast("Action failed"); } finally { setActLoading(false); }
  };

  const doResolve = async () => {
    if (!selAlert) return;
    setActLoading(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      await fetch(`${API}/alerts/${selAlert.id}/resolve`, {
        method: "POST", headers,
        body: JSON.stringify({ acknowledged_by: actor, notes: "" }),
      });
      setLiveAlerts(a => a.filter(x => x.id !== selAlert.id));
      setSelAlert(null);
      showToast("Alert resolved");
    } catch { showToast("Action failed"); } finally { setActLoading(false); }
  };

  const doMute = async () => {
    if (!selAlert) return;
    setActLoading(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      await fetch(`${API}/blocklist`, {
        method: "POST", headers,
        body: JSON.stringify({ charger_sn: selAlert.charger_sn, reason: "Muted from Alerts tab", blocked_by: actor }),
      });
      setLiveAlerts(a => a.filter(x => x.charger_sn !== selAlert.charger_sn));
      setSelAlert(null);
      showToast("Charger muted");
    } catch { showToast("Action failed"); } finally { setActLoading(false); }
  };

  const doAssign = async () => {
    if (!selAlert || !assignInput.trim()) return;
    setActLoading(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      await fetch(`${API}/alerts/${selAlert.id}/assign`, {
        method: "POST", headers,
        body: JSON.stringify({ assigned_to: assignInput.trim(), actor }),
      });
      setLiveAlerts(a => a.map(x => x.id === selAlert.id ? { ...x, assigned_to: assignInput.trim() } : x));
      setSelAlert(s => ({ ...s, assigned_to: assignInput.trim() }));
      await fetchEvents(selAlert.id);
      showToast(`Assigned to ${assignInput.trim()}`);
    } catch { showToast("Action failed"); } finally { setActLoading(false); }
  };

  const doNote = async () => {
    if (!selAlert || !noteInput.trim()) return;
    setActLoading(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      await fetch(`${API}/alerts/${selAlert.id}/note`, {
        method: "POST", headers,
        body: JSON.stringify({ note: noteInput.trim(), actor }),
      });
      setNoteInput("");
      await fetchEvents(selAlert.id);
      showToast("Note added");
    } catch { showToast("Action failed"); } finally { setActLoading(false); }
  };

  const doGenerateTicket = async () => {
    if (!selAlert) return;
    setActLoading(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const res = await fetch(`${API}/charger/${selAlert.charger_sn}/generate-ticket`, {
        method: "POST", headers, body: JSON.stringify({}),
      });
      if (res.status === 404) {
        setTicketMsg("Ticket generation coming soon");
      } else if (res.ok) {
        const data = await res.json();
        setTicketMsg(data.draft || JSON.stringify(data));
        await fetch(`${API}/alerts/${selAlert.id}/note`, {
          method: "POST", headers: { ...(await authHeaders()), "Content-Type": "application/json" },
          body: JSON.stringify({ note: "Ticket draft generated", actor, event_type: "ticket_drafted" }),
        });
        await fetchEvents(selAlert.id);
      } else {
        setTicketMsg("Ticket generation coming soon");
      }
    } catch { setTicketMsg("Ticket generation coming soon"); } finally { setActLoading(false); }
  };

  // ── Rules actions ─────────────────────────────────────────────────────────

  const doCreateRule = async () => {
    if (!newRule.name.trim()) return;
    setRulesSaving(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      await fetch(`${API}/alert-rules`, { method: "POST", headers, body: JSON.stringify(newRule) });
      setNewRule({ name: "", min_priority: "P2", alert_types: [], min_risk_score: 0, min_state_score: 0, channel_slack: 0, channel_dashboard: 1, channel_email: "" });
      await fetchRules();
      showToast("Rule created");
    } catch { showToast("Failed to create rule"); } finally { setRulesSaving(false); }
  };

  const doToggleRule = async (rule) => {
    const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
    await fetch(`${API}/alert-rules/${rule.id}`, {
      method: "PUT", headers,
      body: JSON.stringify({ ...rule, enabled: rule.enabled ? 0 : 1 }),
    });
    await fetchRules();
  };

  const doDeleteRule = async (id) => {
    const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
    await fetch(`${API}/alert-rules/${id}`, { method: "DELETE", headers });
    await fetchRules();
    showToast("Rule deleted");
  };

  const doSaveEdit = async () => {
    if (!editingRule) return;
    setRulesSaving(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      await fetch(`${API}/alert-rules/${editingRule.id}`, { method: "PUT", headers, body: JSON.stringify(editingRule) });
      setEditingRule(null);
      await fetchRules();
      showToast("Rule updated");
    } catch { showToast("Failed to update"); } finally { setRulesSaving(false); }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const filteredLive = liveAlerts.filter(a => {
    if (priFilter  !== "ALL" && !a.priority_tier?.startsWith(priFilter))  return false;
    if (typeFilter !== "ALL" && a.alert_type !== typeFilter)               return false;
    if (assignFilter === "ASSIGNED"   && !a.assigned_to) return false;
    if (assignFilter === "UNASSIGNED" &&  a.assigned_to) return false;
    if (timeFilter === "24HR") {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      if (_parseTs(a.triggered_at) < cutoff) return false;
    } else if (timeFilter === "7D") {
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      if (_parseTs(a.triggered_at) < cutoff) return false;
    }
    if (alertSearch) {
      const q = alertSearch.toLowerCase();
      const matchesSn   = a.charger_sn?.toLowerCase().includes(q);
      const matchesSid  = a.station_id && a.station_id !== "—" && a.station_id.toLowerCase().includes(q);
      const matchesSite = a.site_name?.toLowerCase().includes(q);
      if (!matchesSn && !matchesSid && !matchesSite) return false;
    }
    return true;
  });

  // ── Shared styles ─────────────────────────────────────────────────────────

  const cardSt = { background: D.card, border: `1px solid ${D.border}`, borderRadius: 6 };
  const btnSt  = (active, color) => ({
    fontSize: 10, fontWeight: 700, letterSpacing: "0.07em",
    padding: "4px 10px", borderRadius: 4, cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    background: active ? color : "transparent",
    border: `1px solid ${active ? color : D.border}`,
    color: active ? "#000" : D.muted,
  });
  const inputSt = {
    background: D.card, border: `1px solid ${D.border}`, borderRadius: 4,
    color: D.text, fontSize: 11, padding: "5px 10px",
    fontFamily: "'DM Mono', monospace", outline: "none",
  };
  const actionBtnSt = (color) => ({
    fontSize: 10, fontWeight: 700, padding: "5px 12px", borderRadius: 4,
    cursor: actLoading ? "not-allowed" : "pointer", border: `1px solid ${color}`,
    background: `${color}18`, color, fontFamily: "'DM Mono', monospace",
    opacity: actLoading ? 0.6 : 1,
  });

  // ── Sub-view renderers ────────────────────────────────────────────────────

  const renderAlertRow = (a, isSelected, onClick) => {
    const tc = darkTierColor(a.priority_tier, D);
    return (
      <div key={a.id} onClick={onClick} style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        padding: "8px 14px", cursor: "pointer",
        borderBottom: `1px solid ${D.border}`,
        background: isSelected ? "#111" : "transparent",
        borderLeft: `3px solid ${isSelected ? tc : "transparent"}`,
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, border: `1px solid ${tc}`, color: tc, flexShrink: 0, marginTop: 1, fontFamily: "'DM Mono', monospace" }}>
          {(a.priority_tier || "?").slice(0, 2)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: D.text, fontFamily: "'DM Mono', monospace", marginBottom: 1 }}>
            {a.station_id && a.station_id !== "—" ? a.station_id : a.charger_sn}
            {a.site_name && <span style={{ fontSize: 11, color: D.muted, fontWeight: 400, marginLeft: 8 }}>{a.site_name}</span>}
          </div>
          <div style={{ fontSize: 10, color: D.muted, fontFamily: "'DM Mono', monospace" }}>
            {a.alert_type} · {((a.fault_probability || 0) * 100).toFixed(1)}%
            {a.assigned_to && <span style={{ color: D.P4, marginLeft: 8 }}>→ {a.assigned_to}</span>}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: tc, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{((a.fault_probability || 0) * 100).toFixed(0)}%</div>
          <div style={{ fontSize: 9, color: D.label, marginTop: 2 }}>{timeAgo(a.triggered_at)}</div>
          <div style={{ fontSize: 8, color: D.label, marginTop: 1 }}>{fmtAlertDate(a.triggered_at)}</div>
          {a.status === "acknowledged" && <div style={{ fontSize: 9, color: D.P3, marginTop: 1 }}>ACK'd</div>}
        </div>
      </div>
    );
  };

  const renderRuleForm = (rule, setRule, onSave, onCancel, saving) => (
    <div style={{ ...cardSt, padding: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: D.label, marginBottom: 4, fontFamily: "'DM Mono', monospace" }}>NAME</div>
          <input value={rule.name} onChange={e => setRule(r => ({ ...r, name: e.target.value }))}
            placeholder="Rule name" style={{ ...inputSt, width: "100%" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: D.label, marginBottom: 4, fontFamily: "'DM Mono', monospace" }}>MIN PRIORITY</div>
            <select value={rule.min_priority} onChange={e => setRule(r => ({ ...r, min_priority: e.target.value }))}
              style={{ ...inputSt, width: "100%" }}>
              {["P1","P2","P3","P4"].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: D.label, marginBottom: 4, fontFamily: "'DM Mono', monospace" }}>CHANNELS</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[["Slack", "channel_slack"], ["Dashboard", "channel_dashboard"]].map(([lbl, key]) => (
                <label key={key} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 10, color: rule[key] ? D.P4 : D.muted, fontFamily: "'DM Mono', monospace" }}>
                  <input type="checkbox" checked={!!rule[key]} onChange={e => setRule(r => ({ ...r, [key]: e.target.checked ? 1 : 0 }))}
                    style={{ accentColor: D.P4 }} />
                  {lbl}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: D.label, marginBottom: 6, fontFamily: "'DM Mono', monospace" }}>ALERT TYPES (empty = any)</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {ALERT_TYPE_OPTIONS.map(t => {
              const active = (rule.alert_types || []).includes(t);
              return (
                <button key={t} onClick={() => setRule(r => ({
                  ...r, alert_types: active
                    ? (r.alert_types || []).filter(x => x !== t)
                    : [...(r.alert_types || []), t],
                }))} style={{ fontSize: 9, padding: "3px 8px", borderRadius: 3, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontWeight: 700, border: `1px solid ${active ? D.P4 : D.border}`, background: active ? `${D.P4}22` : "transparent", color: active ? D.P4 : D.muted }}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[["MIN RISK SCORE", "min_risk_score"], ["MIN STATE SCORE", "min_state_score"]].map(([lbl, key]) => (
            <div key={key}>
              <div style={{ fontSize: 10, color: D.label, marginBottom: 4, fontFamily: "'DM Mono', monospace" }}>
                {lbl}: <span style={{ color: D.P4 }}>{Math.round((rule[key] || 0) * 100)}%</span>
              </div>
              <input type="range" min="0" max="1" step="0.05" value={rule[key] || 0}
                onChange={e => setRule(r => ({ ...r, [key]: parseFloat(e.target.value) }))}
                style={{ width: "100%", accentColor: D.P4 }} />
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 10, color: D.label, marginBottom: 4, fontFamily: "'DM Mono', monospace" }}>
            EMAIL RECIPIENTS <span style={{ color: D.muted, fontWeight: 400 }}>(comma-separated)</span>
          </div>
          <input
            value={rule.channel_email || ""}
            onChange={e => setRule(r => ({ ...r, channel_email: e.target.value }))}
            placeholder="ops@evcs.com, manager@evcs.com"
            style={{ ...inputSt, width: "100%" }}
          />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {onCancel && <button onClick={onCancel} style={{ ...btnSt(false, D.muted), padding: "5px 14px" }}>CANCEL</button>}
          <button onClick={onSave} disabled={saving || !rule.name.trim()} style={{ fontSize: 10, fontWeight: 700, padding: "5px 14px", borderRadius: 4, cursor: saving ? "not-allowed" : "pointer", border: `1px solid ${D.P4}`, background: `${D.P4}22`, color: D.P4, fontFamily: "'DM Mono', monospace", opacity: saving ? 0.6 : 1 }}>
            {saving ? "SAVING..." : "SAVE RULE"}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div style={{ background: D.bg, minHeight: "calc(100vh - 57px)", color: D.text, fontFamily: "'DM Mono', monospace" }}>
      <style>{`
        .dark-row:hover { background: #111 !important; }
        .dark-row-hover:hover { background: #0f0f0f !important; }
      `}</style>

      {/* Sub-view tabs */}
      <div style={{ borderBottom: `1px solid ${D.border}`, padding: "0 32px", display: "flex", gap: 0, background: D.card }}>
        {["LIVE","RULES","HISTORY"].map(sv => (
          <button key={sv} onClick={() => setSubView(sv)} style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
            padding: "11px 20px", cursor: "pointer", border: "none", background: "transparent",
            color: subView === sv ? D.text : D.muted,
            borderBottom: `2px solid ${subView === sv ? D.P4 : "transparent"}`,
            marginBottom: -1, fontFamily: "'DM Mono', monospace",
          }}>{sv}</button>
        ))}
      </div>

      <div style={{ padding: "16px 24px" }}>

        {/* ── LIVE ── */}
        {subView === "LIVE" && (
          <div>
            <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 10, color: D.label }}>PRIORITY:</span>
              {["ALL","P1","P2","P3","P4"].map(p => (
                <button key={p} onClick={() => setPriFilter(p)} style={btnSt(priFilter === p, p === "ALL" ? D.P4 : darkTierColor(p, D))}>{p}</button>
              ))}
              <span style={{ fontSize: 10, color: D.label, marginLeft: 8 }}>TYPE:</span>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                style={{ ...inputSt, padding: "3px 8px", fontSize: 10 }}>
                <option value="ALL">ALL TYPES</option>
                {ALERT_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span style={{ fontSize: 10, color: D.label, marginLeft: 8 }}>ASSIGNED:</span>
              {[["ALL","ALL"],["ASSIGNED","ASSIGNED"],["UNASSIGNED","UNASSIGNED"]].map(([lbl, val]) => (
                <button key={val} onClick={() => setAssignFilter(val)} style={btnSt(assignFilter === val, D.P4)}>{lbl}</button>
              ))}
              <span style={{ fontSize: 10, color: D.label, marginLeft: 8 }}>TIME:</span>
              {[["ALL","ALL"],["24HR","24HR"],["7D","7D"]].map(([lbl, val]) => (
                <button key={val} onClick={() => setTimeFilter(val)} style={btnSt(timeFilter === val, D.P4)}>{lbl}</button>
              ))}
              <input
                value={alertSearch}
                onChange={e => setAlertSearch(e.target.value)}
                placeholder="search station, site..."
                style={{ ...inputSt, marginLeft: 8, width: 180, fontSize: 10, padding: "3px 8px" }}
              />
              <span style={{ fontSize: 10, color: D.muted, marginLeft: "auto" }}>
                {liveLoading ? "refreshing…" : `${filteredLive.length} alert${filteredLive.length !== 1 ? "s" : ""}`}
              </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: selAlert ? "minmax(260px, 34%) minmax(520px, 1fr)" : "1fr", gap: 16, alignItems: "start" }}>
              <div style={{ ...cardSt, overflow: "hidden" }}>
                <div style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto" }}>
                  {filteredLive.length === 0 ? (
                    <div style={{ padding: 32, textAlign: "center", fontSize: 11, color: D.muted }}>
                      {liveLoading ? "Loading alerts..." : "No active alerts matching filters"}
                    </div>
                  ) : filteredLive.map(a => renderAlertRow(a, selAlert?.id === a.id, () => setSelAlert(a)))}
                </div>
              </div>
              {selAlert && (
                <div style={{ position: "sticky", top: 24 }}>
                  <div style={{ ...cardSt, padding: "10px 14px", marginBottom: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: darkTierColor(selAlert.priority_tier, D), fontFamily: "'DM Mono', monospace", marginRight: 4 }}>
                      {(selAlert.priority_tier || "?").slice(0, 2)} · {selAlert.alert_type}
                    </span>
                    {selAlert.status === "active" && <button onClick={doAck} style={actionBtnSt(D.P4)}>✓ ACK</button>}
                    <button onClick={doResolve} style={actionBtnSt(D.P2)}>RESOLVE</button>
                    <button onClick={doMute}    style={actionBtnSt(D.muted)}>MUTE</button>
                    <button onClick={doGenerateTicket} style={actionBtnSt("#9B59B6")}>TICKET</button>
                    <button onClick={() => setSelAlert(null)} style={{ background: "none", border: "none", color: D.muted, cursor: "pointer", marginLeft: "auto", fontSize: 14 }}>✕</button>
                  </div>
                  <div style={{ ...cardSt, padding: "8px 14px", marginBottom: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input value={assignInput} onChange={e => setAssignInput(e.target.value)}
                      placeholder="Assign to…" style={{ ...inputSt, flex: 1, minWidth: 120 }} />
                    <button onClick={doAssign} style={{ ...actionBtnSt(D.P4), padding: "5px 8px", flexShrink: 0 }}>SET</button>
                    <input value={noteInput} onChange={e => setNoteInput(e.target.value)}
                      placeholder="Add note…" style={{ ...inputSt, flex: 2, minWidth: 160 }} />
                    <button onClick={doNote} style={{ ...actionBtnSt(D.P4), padding: "5px 8px", flexShrink: 0 }}>NOTE</button>
                  </div>
                  {ticketMsg && (
                    <div style={{ margin: "0 0 10px", padding: "8px 12px", background: "#1a0a2e", border: "1px solid #9B59B6", borderRadius: 4, fontSize: 11, color: "#C39BD3", fontFamily: "'DM Mono', monospace" }}>
                      {ticketMsg}
                    </div>
                  )}
                  {alertChargerLoading ? (
                    <div style={{ ...cardSt, padding: 32, textAlign: "center", fontSize: 11, color: D.muted }}>Loading charger details…</div>
                  ) : alertCharger ? (
                    <DetailPanel charger={alertCharger} onClose={() => setSelAlert(null)} />
                  ) : (
                    <div style={{ ...cardSt, padding: 32, textAlign: "center", fontSize: 11, color: D.muted }}>Charger details unavailable</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── RULES ── */}
        {subView === "RULES" && (
          <div style={{ maxWidth: 760 }}>
            <div style={{ fontSize: 11, color: D.muted, marginBottom: 16 }}>
              Rules control which alerts appear in the dashboard and trigger Slack. If no rules exist, all alerts are shown and P1/P2 go to Slack.
            </div>
            {rulesLoading ? (
              <div style={{ fontSize: 11, color: D.muted, marginBottom: 16 }}>Loading rules...</div>
            ) : rules.length === 0 ? (
              <div style={{ fontSize: 11, color: D.label, marginBottom: 16 }}>No rules configured — using defaults (show all, Slack P1/P2).</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                {rules.map(rule => editingRule?.id === rule.id ? (
                  <div key={rule.id}>
                    {renderRuleForm(editingRule, setEditingRule, doSaveEdit, () => setEditingRule(null), rulesSaving)}
                  </div>
                ) : (
                  <div key={rule.id} style={{ ...cardSt, padding: "12px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: rule.enabled ? D.text : D.muted }}>{rule.name}</span>
                        <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 10, border: `1px solid ${rule.enabled ? D.P4 : D.border}`, color: rule.enabled ? D.P4 : D.muted }}>{rule.enabled ? "ENABLED" : "DISABLED"}</span>
                      </div>
                      <div style={{ fontSize: 10, color: D.muted }}>{ruleDescription(rule)}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => doToggleRule(rule)} style={btnSt(rule.enabled, D.P4)}>{rule.enabled ? "DISABLE" : "ENABLE"}</button>
                      <button onClick={() => setEditingRule({ ...rule })} style={btnSt(false, D.muted)}>EDIT</button>
                      <button onClick={() => doDeleteRule(rule.id)} style={btnSt(false, D.P1)}>DEL</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 10, color: D.P4, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10 }}>NEW RULE</div>
            {renderRuleForm(newRule, setNewRule, doCreateRule, null, rulesSaving)}
          </div>
        )}

        {/* ── HISTORY ── */}
        {subView === "HISTORY" && (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
              <input
                value={histSearch}
                onChange={e => setHistSearch(e.target.value)}
                placeholder="search station, site..."
                style={{ ...inputSt, width: 200, fontSize: 10, padding: "3px 8px" }}
              />
              <span style={{ fontSize: 10, color: D.muted }}>
                {histLoading ? "loading…" : (() => {
                  const q = histSearch.toLowerCase();
                  const n = q ? history.filter(a =>
                    a.charger_sn?.toLowerCase().includes(q) ||
                    (a.station_id && a.station_id !== "—" && a.station_id.toLowerCase().includes(q)) ||
                    a.site_name?.toLowerCase().includes(q)
                  ).length : history.length;
                  return `${n} resolved`;
                })()}
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: selHistory ? "minmax(260px, 34%) minmax(520px, 1fr)" : "1fr", gap: 16, alignItems: "start" }}>
              <div style={{ ...cardSt, overflow: "hidden" }}>
                <div style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto" }}>
                  {histLoading ? (
                    <div style={{ padding: 32, textAlign: "center", fontSize: 11, color: D.muted }}>Loading...</div>
                  ) : history.length === 0 ? (
                    <div style={{ padding: 32, textAlign: "center", fontSize: 11, color: D.muted }}>No resolved alerts</div>
                  ) : history.filter(a => {
                    if (!histSearch) return true;
                    const q = histSearch.toLowerCase();
                    return a.charger_sn?.toLowerCase().includes(q) ||
                      (a.station_id && a.station_id !== "—" && a.station_id.toLowerCase().includes(q)) ||
                      a.site_name?.toLowerCase().includes(q);
                  }).map(a => {
                    const tc = darkTierColor(a.priority_tier, D);
                    const isSelected = selHistory?.id === a.id;
                    return (
                      <div key={a.id} onClick={() => setSelHistory(a)} className="dark-row" style={{
                        display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 16px",
                        borderBottom: `1px solid ${D.border}`, cursor: "pointer",
                        background: isSelected ? "#111" : "transparent",
                        borderLeft: `3px solid ${isSelected ? tc : "transparent"}`,
                      }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, border: `1px solid ${tc}40`, color: tc, flexShrink: 0, marginTop: 1 }}>
                          {(a.priority_tier || "?").slice(0, 2)}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: D.text, marginBottom: 1 }}>
                            {a.station_id && a.station_id !== "—" ? a.station_id : a.charger_sn}
                            {a.site_name && <span style={{ fontSize: 11, color: D.muted, fontWeight: 400, marginLeft: 8 }}>{a.site_name}</span>}
                          </div>
                          <div style={{ fontSize: 10, color: D.muted }}>{a.alert_type} · triggered {timeAgo(a.triggered_at)}</div>
                          <div style={{ fontSize: 9, color: D.label, marginTop: 1 }}>{fmtAlertDate(a.triggered_at)}</div>
                          {a.acknowledged_by && (
                            <div style={{ fontSize: 10, color: D.label, marginTop: 2 }}>
                              resolved by {a.acknowledged_by}
                              {a.acknowledged_at ? ` · ${timeAgo(a.acknowledged_at)}` : ""}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, border: `1px solid ${D.border}`, color: D.muted }}>RESOLVED</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {selHistory && (
                <div style={{ position: "sticky", top: 24 }}>
                  <div style={{ ...cardSt, padding: "10px 14px", marginBottom: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: darkTierColor(selHistory.priority_tier, D), fontFamily: "'DM Mono', monospace", marginRight: 4 }}>
                      {(selHistory.priority_tier || "?").slice(0, 2)} · {selHistory.alert_type}
                    </span>
                    <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 3, border: `1px solid ${D.border}`, color: D.muted }}>RESOLVED</span>
                    {selHistory.acknowledged_by && (
                      <span style={{ fontSize: 9, color: D.label, marginLeft: 4 }}>by {selHistory.acknowledged_by}</span>
                    )}
                    <button onClick={() => setSelHistory(null)} style={{ background: "none", border: "none", color: D.muted, cursor: "pointer", marginLeft: "auto", fontSize: 14 }}>✕</button>
                  </div>
                  <div style={{ ...cardSt, padding: "10px 14px", marginBottom: 10, maxHeight: 180, overflowY: "auto" }}>
                    <div style={{ fontSize: 10, color: D.label, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>EVENT TIMELINE</div>
                    <EventTimeline events={histEvents} theme={D} />
                  </div>
                  {histChargerLoading ? (
                    <div style={{ ...cardSt, padding: 32, textAlign: "center", fontSize: 11, color: D.muted }}>Loading charger details…</div>
                  ) : histCharger ? (
                    <DetailPanel charger={histCharger} onClose={() => setSelHistory(null)} />
                  ) : (
                    <div style={{ ...cardSt, padding: 32, textAlign: "center", fontSize: 11, color: D.muted }}>Charger details unavailable</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: D.card, border: `1px solid ${D.P4}`, color: D.P4, padding: "10px 20px", borderRadius: 6, fontSize: 11, zIndex: 9999, fontFamily: "'DM Mono', monospace", boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
          {toast}
        </div>
      )}
    </div>
  );
}

export default AlertsTab;
